import { useCallback } from "react";
import { useRefreshCounter } from "./api.utils";
import {
  ConstructionProjectMutableProperties,
  ConstructionSiteMutableProperties,
  PagedResponse,
  Project
} from "./models";
import {
  ApiCall,
  ApiErrorResponse,
  PagedApiState,
  UpdateResult,
  useAuthorizedFetch,
  useGetAuthorized,
  usePagedApiState,
} from "./useApiState";

export function useConstructionProjects(
  ignorePagination: boolean
): PagedApiState<Project> {
  const refreshCounter = useRefreshCounter();

  let url = "construction-projects";

  // Pagination is setting the take and skip query params, which for use cases like
  // construction projects is not needed. Remove them from the url before making the
  // request.
  const removeQueryParams = useCallback((url: string): string => {
    return url.split("?")[0];
  }, []);

  return usePagedApiState(
    useGetAuthorized<PagedResponse<Project>>(
      url,
      undefined,
      ignorePagination ? removeQueryParams : undefined
    ),
    refreshCounter
  );
}

export function useUpdateConstructionProject(
  constructionProjectId: string
): ApiCall<ConstructionProjectMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch();
  return async (constructionProject: ConstructionProjectMutableProperties) => {
    const result = await authorizedFetch(
      `construction-projects/${constructionProjectId}`,
      { method: "put", body: JSON.stringify(constructionProject) }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Construction project updated successfully" },
        },
      };
    }
  };
}

export function useAssignConstructionSite(): ApiCall<
  { constructionSiteId?: string; beaconIds: string[]; exclusive?: boolean },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (assignment: {
    constructionSiteId?: string;
    beaconIds: string[];
  }) => {
    const body = {
      name: {},
      project: {},
      address: {},
      beacons: assignment.beaconIds,
    };
    const url = `construction-sites/${assignment.constructionSiteId}`;

    const result = await authorizedFetch(url, {
      method: "put",
      body: JSON.stringify(body),
    });
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useUpdateConstructionSite(
  constructionSiteId: string
): ApiCall<ConstructionSiteMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch();
  return async (constructionSite: ConstructionSiteMutableProperties) => {
    const result = await authorizedFetch(
      `construction-sites/${constructionSiteId}`,
      { method: "put", body: JSON.stringify(constructionSite) }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Construction site updated successfully" },
        },
      };
    }
  };
}

export function useDeleteProject(
  projectId: string
): ApiCall<void, void> {
  const authorizedFetch = useAuthorizedFetch();
  return async () => {
    const result = await authorizedFetch(`construction-project/${projectId}`, {
      method: "delete",
    });
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useDeleteSite(siteId: string): ApiCall<void, void> {
  const authorizedFetch = useAuthorizedFetch();
  return async () => {
    const result = await authorizedFetch(`construction-sites/${siteId}`, {
      method: "delete",
    });
    if (!result.ok) {
      throw result.status;
    }
  };
}